import React from 'react';
import { List, ListItem, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getByDisplayKey } from '../utils/displayKeyUtil';
import { htmlStringToText } from '../utils/dataUtil';

/**
 * @typedef {object} FieldConfig
 * @property {string} label The field label.
 * @property {string} value Display key to get the field value.
 * @property {string} vanityLabel Optional translation key to map string field values to alternative text.
 * @property {boolean} note Whether to display the field using special note format.
 * @property {Function} textTransform Optional function to modify the field value. Assumes the value is a string.
 */

/**
 * A component to display a list of label/value pairs inside a list format.
 * Uses display keys to get values from a PS entry object.
 * @param {object} props Props accepted by the component.
 * @param {string} props.testID Test ID to be used.
 * @param {object} props.entry The PS entry to use.
 * @param {Array<FieldConfig>} props.fields The fields to display. A list of <label, value> objects, where the label is a string
 * @param {boolean} props.trailingDivider Whether to show a divider after the final list item.
 * @returns {import('react').FunctionComponent} React element that contains the list.
 */
function LabelValueList({ testID, entry, fields, trailingDivider }) {
  const [translate, i18n] = useTranslation();

  const filteredFields = fields
    .map((field) => {
      const value = getByDisplayKey(entry, field.value, '', i18n.language);
      return { field, value };
    })
    .filter((item) => item.value);

  return (
    <List id={testID} sx={{ py: 0 }}>
      {filteredFields.map(({ field, value }, index) => {
        const content = (
          <Typography
            id={`${testID}-Item-${index}-ValueText`}
            variant="body_md"
            sx={{
              color: 'primary.main',
              whiteSpace: field.note ? 'preserve nowrap' : 'normal',
              ...(field.textTransform && field.textTransform(value)),
            }}
          >
            {field.vanityLabel
              ? translate(`${field.vanityLabel}.${value}`)
              : htmlStringToText(value).join('\n')}
          </Typography>
        );

        return (
          <ListItem
            id={`${testID}-Item-${index}`}
            key={field.label}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              px: 0,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
            divider={trailingDivider || index < filteredFields.length - 1}
          >
            <Typography
              id={`${testID}-Item-${index}-LabelText`}
              variant="body_sm"
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            >
              {translate(field.label)}
            </Typography>
            {field.note ? (
              <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>{content}</Box>
            ) : (
              content
            )}
          </ListItem>
        );
      })}
    </List>
  );
}

export default LabelValueList;
